import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { useTenantStore } from "@/store/tenant";

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: "active" | "suspended" | "trial";
  created_at: string;
  expires_at: string | null;
  camera_count: number;
  user_count: number;
}

export interface TenantDetail extends TenantSummary {
  settings_json: Record<string, unknown>;
}

const key = ["admin", "tenants"] as const;

export function useAdminTenants() {
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data } = await api.get<TenantSummary[]>("/api/v1/admin/tenants");
      return data;
    },
  });
}

export function useAdminTenant(id: string | undefined) {
  return useQuery({
    queryKey: [...key, id],
    queryFn: async () => {
      const { data } = await api.get<TenantDetail>(
        `/api/v1/admin/tenants/${id}`,
      );
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      slug: string;
      plan: string;
      expires_at?: string | null;
    }) => {
      const { data } = await api.post<TenantDetail>(
        "/api/v1/admin/tenants",
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useUpdateTenant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<TenantDetail>) => {
      const { data } = await api.put<TenantDetail>(
        `/api/v1/admin/tenants/${id}`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useSuspendTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<TenantDetail>(
        `/api/v1/admin/tenants/${id}/suspend`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useReactivateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<TenantDetail>(
        `/api/v1/admin/tenants/${id}/reactivate`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export interface TenantSettings {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  expires_at: string | null;
  // v0.14.1 — IANA timezone string (e.g. "Asia/Kolkata").
  timezone: string;
  settings_json: Record<string, unknown>;
}

export function useTenantSettings() {
  return useQuery({
    queryKey: ["tenant", "settings"],
    queryFn: async () => {
      const { data } = await api.get<TenantSettings>("/api/v1/tenant/settings");
      // v0.14.1 — mirror the tenant timezone into the Zustand store
      // so formatTimestamp() picks it up across the whole app.
      useTenantStore.getState().setTimezone(data.timezone || "Asia/Kolkata");
      return data;
    },
  });
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name?: string;
      timezone?: string;
      settings_json?: Record<string, unknown>;
    }) => {
      const { data } = await api.put<TenantSettings>(
        "/api/v1/tenant/settings",
        input,
      );
      // v0.14.1 — immediately reflect the new timezone in the store
      // so timestamps re-render without a page reload.
      useTenantStore.getState().setTimezone(data.timezone || "Asia/Kolkata");
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenant", "settings"] });
    },
  });
}
